import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Block } from '@blogforge/shared';
import { api } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';

type Theme = {
  colorPrimary: string;
  colorText: string;
  colorBackground: string;
  colorSurface: string;
  colorBorder: string;
  fontFamily: string;
  fontSizeBase: string;
  lineHeightBase: string;
  spacingBase: string;
  radiusBase: string;
};

// Color input with preview
const ColorInput = ({
  label,
  themeKey,
  theme,
  updateTheme,
}: {
  label: string;
  themeKey: keyof Theme;
  theme: Theme;
  updateTheme: (key: keyof Theme, val: string) => void;
}) => {
  return (
    <div>
      <label className="field-label">{label}</label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          id={`${themeKey}-picker`}
          value={theme[themeKey] as string}
          onChange={(e) => updateTheme(themeKey, e.target.value)}
          className="h-10 w-10 cursor-pointer rounded-lg border border-line-strong bg-bg-soft p-1"
        />
        <input
          type="text"
          id={`${themeKey}-text`}
          value={theme[themeKey] as string}
          onChange={(e) => updateTheme(themeKey, e.target.value)}
          className="field-input flex-1 font-mono text-sm"
        />
      </div>
    </div>
  );
};

export function SettingsPage() {
  const qc = useQueryClient();
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  // Site settings belong to a workspace, so the active one is part of the key:
  // the query must never answer for the workspace the user just left.
  const { data: site } = useQuery({
    queryKey: ['site-settings', activeWorkspaceId],
    queryFn: () => api.settings.getSite(),
    enabled: Boolean(activeWorkspaceId),
  });

  const s = site as Record<string, unknown> | undefined;

  const [siteName, setSiteName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [siteDesc, setSiteDesc] = useState('');
  const [analytics, setAnalytics] = useState('');

  // Site-level email footer details. These fields are no longer composed into
  // fixed markup — they are the data behind the system variables
  // ({{tagline}}, {{logo_url}}, {{socials}}, {{address}}, {{legal_text}}) that
  // the footer *content* references. The content itself is authored in the
  // footer editor (/settings/email-footer).
  const [footerTagline, setFooterTagline] = useState('');
  const [footerLogoUrl, setFooterLogoUrl] = useState('');
  const [footerLogoAlt, setFooterLogoAlt] = useState('');
  const [footerAddress, setFooterAddress] = useState('');
  const [footerLegal, setFooterLegal] = useState('');
  const [footerShowUnsubscribe, setFooterShowUnsubscribe] = useState(true);
  const [footerSocials, setFooterSocials] = useState<{ platform: string; url: string }[]>([]);
  /**
   * The authored footer blocks, carried through untouched on save: the settings
   * route replaces `emailFooter` wholesale, so a details save that dropped
   * `blocks` would wipe the footer the user built in the footer editor.
   * `null` = the loaded footer had no authored content at all.
   */
  const [footerBlocks, setFooterBlocks] = useState<Block[] | null>(null);

  const [theme, setTheme] = useState({
    // Colors
    colorPrimary: '#3b82f6',
    colorText: '#111827',
    colorBackground: '#ffffff',
    colorSurface: '#f3f4f6',
    colorBorder: '#e5e7eb',

    // Typography
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSizeBase: '17px',
    lineHeightBase: '1.75',

    // Spacing
    spacingBase: '1.5rem',

    // Radius
    radiusBase: '6px',
  });

  useEffect(() => {
    if (!s) return;
    setSiteName((s['siteName'] as string) ?? '');
    setBaseUrl((s['baseUrl'] as string) ?? '');
    setSiteDesc((s['siteDescription'] as string) ?? '');
    setAnalytics((s['analyticsSnippet'] as string) ?? '');

    // Load theme from site settings if available
    if (s['defaultTheme']) {
      const savedTheme = s['defaultTheme'] as Record<string, string>;
      setTheme((prev) => ({
        ...prev,
        ...savedTheme,
      }));
    }

    // Load the email footer details, and the authored content alongside them.
    const footer = (s['emailFooter'] as Record<string, unknown> | null) ?? {};
    setFooterTagline((footer['tagline'] as string) ?? '');
    setFooterLogoUrl((footer['logoUrl'] as string) ?? '');
    setFooterLogoAlt((footer['logoAlt'] as string) ?? '');
    setFooterAddress((footer['address'] as string) ?? '');
    setFooterLegal((footer['legalText'] as string) ?? '');
    setFooterShowUnsubscribe((footer['showUnsubscribe'] as boolean) ?? true);
    setFooterSocials(
      ((footer['socials'] as { platform: string; url: string }[] | null) ?? []).map((x) => ({
        platform: x.platform ?? '',
        url: x.url ?? '',
      })),
    );
    setFooterBlocks((footer['blocks'] as Block[] | undefined) ?? null);
  }, [s, activeWorkspaceId]);

  // Generate CSS variables for preview
  const cssVars = `
    :root {
      --color-primary: ${theme.colorPrimary};
      --color-text: ${theme.colorText};
      --color-background: ${theme.colorBackground};
      --color-surface: ${theme.colorSurface};
      --color-border: ${theme.colorBorder};
      --font-body: ${theme.fontFamily};
      --font-heading: ${theme.fontFamily};
      --font-size-base: ${theme.fontSizeBase};
      --line-height-base: ${theme.lineHeightBase};
      --spacing-base: ${theme.spacingBase};
      --radius-base: ${theme.radiusBase};
    }
  `;

  const saveMutation = useMutation({
    mutationFn: () =>
      api.settings.updateSite({
        siteName,
        baseUrl,
        siteDescription: siteDesc,
        analyticsSnippet: analytics || null,
        activeThemeId: (s?.['activeThemeId'] as string) ?? 'minimal',
        theme,
        emailFooter: {
          // PUT replaces the whole footer object, so the authored content the
          // footer editor saved has to travel back with the details — without
          // it, saving a tagline would delete every footer block. `null` means
          // this footer never had any, and omitting the key keeps that true.
          ...(footerBlocks ? { blocks: footerBlocks } : {}),
          tagline: footerTagline || undefined,
          logoUrl: footerLogoUrl || undefined,
          logoAlt: footerLogoAlt || undefined,
          address: footerAddress || undefined,
          legalText: footerLegal || undefined,
          showUnsubscribe: footerShowUnsubscribe,
          socials: footerSocials.filter((x) => x.platform && x.url),
        },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['site-settings'] }),
  });

  // Helper to update theme values
  const updateTheme = (key: string, value: string) => {
    console.log('updateTheme', { key, value });
    setTheme((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-12 px-8 py-10">
        {/* Header */}
        <div className="relative">
          <p className="kicker mb-2">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            Workspace
          </p>
          <h1 className="text-[22px] font-bold tracking-tight text-ink">Settings</h1>
          <p className="mt-1 text-[13px] text-ink-soft">
            Configure your blog's identity, design tokens and analytics.
          </p>
        </div>

        {/* API access */}
        {/*
          Keys are credentials for the whole workspace, so this card is a door
          to its own page (/settings/api) rather than a section here: issuing
          and revoking them is a deliberate action, and the create result has
          to be the only thing on screen when a secret is shown once.
        */}
        <section className="rounded-xl border border-line bg-card p-5">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-ink">API access</h3>
            <Link to="/settings/api" className="btn-primary shrink-0 px-3 py-1.5 text-xs">
              Manage API keys
            </Link>
          </div>
          <p className="text-xs text-muted">
            Issue scoped key pairs so an external system can queue sends and manage subscribers
            without a user session. Each key carries only the permissions you tick for it, and
            can be revoked at any time.
          </p>
        </section>

        {/* Site identity */}
        <section>
          <h2 className="kicker mb-5 flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-accent" />
            Site identity
          </h2>
          <div className="space-y-4">
            <div>
              <label className="field-label">Site name</label>
              <input
                title="Site name input"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                className="field-input"
              />
            </div>
            <div>
              <label className="field-label">Base URL</label>
              <input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                className="field-input font-mono text-sm"
                placeholder="https://blog.yourcompany.com"
              />
              <p className="mt-1 text-xs text-muted">
                Used for canonical URLs and sitemap generation.
              </p>
            </div>
            <div>
              <label className="field-label">Site description</label>
              <textarea
                value={siteDesc}
                onChange={(e) => setSiteDesc(e.target.value)}
                className="field-textarea"
                rows={2}
                placeholder="A short description of your blog."
              />
            </div>
          </div>
        </section>

        {/* Theme / Design Tokens */}
        <section>
          <h2 className="kicker mb-5 flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-accent" />
            Theme & Design Tokens
          </h2>

          {/* Theme Preview */}
          <div
            className="mb-6 overflow-hidden rounded-2xl border border-line shadow-sm"
            style={{
              backgroundColor: theme.colorBackground,
              color: theme.colorText,
              fontFamily: theme.fontFamily,
            }}
          >
            <div className="p-4">
              <h3 className="text-lg font-semibold" style={{ color: theme.colorText }}>
                Preview
              </h3>
              <p
                className="mt-1 text-sm"
                style={{
                  color: theme.colorText,
                  opacity: 0.8,
                }}
              >
                This is how your blog will look with the current theme settings.
              </p>
              <div className="mt-3 flex gap-3">
                <button
                  className="rounded px-4 py-2 text-sm font-semibold text-white"
                  style={{ backgroundColor: theme.colorPrimary }}
                >
                  Primary Button
                </button>
                <button
                  className="rounded border px-4 py-2 text-sm font-semibold"
                  style={{
                    backgroundColor: theme.colorSurface,
                    color: theme.colorText,
                    borderColor: theme.colorBorder,
                  }}
                >
                  Secondary Button
                </button>
              </div>
              <div className="mt-3 rounded p-3" style={{ backgroundColor: theme.colorSurface }}>
                <p className="text-sm" style={{ color: theme.colorText }}>
                  Surface card with{' '}
                  <a href="#" style={{ color: theme.colorPrimary }}>
                    primary link
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Color Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-ink-soft">Colors</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <ColorInput
                label="Primary"
                themeKey="colorPrimary"
                theme={theme}
                updateTheme={updateTheme}
              />
              <ColorInput
                label="Text"
                themeKey="colorText"
                theme={theme}
                updateTheme={updateTheme}
              />
              <ColorInput
                label="Background"
                themeKey="colorBackground"
                theme={theme}
                updateTheme={updateTheme}
              />
              <ColorInput
                label="Surface"
                themeKey="colorSurface"
                theme={theme}
                updateTheme={updateTheme}
              />
              <ColorInput
                label="Border"
                themeKey="colorBorder"
                theme={theme}
                updateTheme={updateTheme}
              />
            </div>
          </div>

          {/* Typography Settings */}
          <div className="mt-6 space-y-4">
            <h3 className="text-sm font-medium text-ink-soft">Typography</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="field-label">Font family</label>
                <input
                  type="text"
                  value={theme.fontFamily}
                  onChange={(e) => updateTheme('fontFamily', e.target.value)}
                  className="field-input font-mono text-sm"
                  placeholder="system-ui, -apple-system, sans-serif"
                />
                <p className="mt-1 text-xs text-muted">CSS font stack for body and headings</p>
              </div>
              <div>
                <label className="field-label">Base font size</label>
                <input
                  type="text"
                  value={theme.fontSizeBase}
                  onChange={(e) => updateTheme('fontSizeBase', e.target.value)}
                  className="field-input font-mono text-sm"
                  placeholder="17px"
                />
              </div>
              <div>
                <label className="field-label">Line height</label>
                <input
                  type="text"
                  value={theme.lineHeightBase}
                  onChange={(e) => updateTheme('lineHeightBase', e.target.value)}
                  className="field-input font-mono text-sm"
                  placeholder="1.75"
                />
              </div>
            </div>
          </div>

          {/* Layout Settings */}
          <div className="mt-6 space-y-4">
            <h3 className="text-sm font-medium text-ink-soft">Layout & Spacing</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="field-label">Base spacing unit</label>
                <input
                  type="text"
                  value={theme.spacingBase}
                  onChange={(e) => updateTheme('spacingBase', e.target.value)}
                  className="field-input font-mono text-sm"
                  placeholder="1.5rem"
                />
              </div>
              <div>
                <label className="field-label">Border radius</label>
                <input
                  type="text"
                  value={theme.radiusBase}
                  onChange={(e) => updateTheme('radiusBase', e.target.value)}
                  className="field-input font-mono text-sm"
                  placeholder="6px"
                />
              </div>
            </div>
          </div>

          {/* CSS Variables Output */}
          <div className="mt-6">
            <details className="overflow-hidden rounded-2xl border border-line bg-card">
              <summary className="cursor-pointer px-5 py-3 text-[13px] font-semibold text-ink-soft transition-colors hover:bg-ink/[0.03] hover:text-ink">
                View CSS variables
              </summary>
              <div className="border-t border-line p-4">
                <pre className="max-h-60 overflow-auto rounded-xl border border-line bg-card-2 p-3.5 font-mono text-[11px] leading-relaxed text-ink-soft">
                  {cssVars}
                </pre>
                <p className="mt-2 text-xs text-muted">
                  These variables are injected into the &lt;head&gt; of every published page.
                </p>
              </div>
            </details>
          </div>
        </section>

        {/* Analytics */}
        <section>
          <h2 className="kicker mb-5 flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-accent" />
            Analytics
          </h2>
          <div>
            <label className="field-label">Analytics snippet</label>
            <textarea
              value={analytics}
              onChange={(e) => setAnalytics(e.target.value)}
              className="field-textarea font-mono text-xs"
              rows={5}
              placeholder={`<!-- Paste your Plausible, Fathom, or GA4 snippet here -->\n<script defer data-domain="yourdomain.com" src="https://plausible.io/js/script.js"></script>`}
              spellCheck={false}
            />
            <p className="mt-1 text-xs text-muted">
              Injected into the &lt;head&gt; of every published post.
            </p>
          </div>
        </section>

        {/* Email footer */}
        <section className="rounded-xl border border-line bg-card p-5">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-ink">Email footer</h3>
            <Link to="/settings/email-footer" className="btn-primary shrink-0 px-3 py-1.5 text-xs">
              Build your footer
            </Link>
          </div>
          <p className="mb-4 text-xs text-muted">
            These details are the <strong>data behind your footer variables</strong>, not the footer
            itself: put <code className="font-mono">{'{{tagline}}'}</code>,{' '}
            <code className="font-mono">{'{{logo_url}}'}</code>,{' '}
            <code className="font-mono">{'{{socials}}'}</code>,{' '}
            <code className="font-mono">{'{{address}}'}</code> or{' '}
            <code className="font-mono">{'{{legal_text}}'}</code> into footer content and it stays
            live as you change them here. Empty fields simply render nothing. The footer content
            itself — its layout, blocks and unsubscribe link — is built in the{' '}
            <Link to="/settings/email-footer" className="font-semibold text-accent hover:text-ink">
              email footer editor
            </Link>
            .
          </p>
          <div className="space-y-3">
            <div>
              <label className="field-label">Tagline</label>
              <input
                className="field-input"
                value={footerTagline}
                placeholder="Fresh ideas, weekly."
                onChange={(e) => setFooterTagline(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="field-label">Logo / signature image URL</label>
                <input
                  className="field-input"
                  value={footerLogoUrl}
                  placeholder="https://…/logo.gif"
                  onChange={(e) => setFooterLogoUrl(e.target.value)}
                />
              </div>
              <div>
                <label className="field-label">Logo alt text</label>
                <input
                  className="field-input"
                  value={footerLogoAlt}
                  placeholder="Acme Inc."
                  onChange={(e) => setFooterLogoAlt(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="field-label">Social links</label>
              {footerSocials.map((social, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    className="field-input w-1/3"
                    value={social.platform}
                    placeholder="X"
                    onChange={(e) =>
                      setFooterSocials((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, platform: e.target.value } : x)),
                      )
                    }
                  />
                  <input
                    className="field-input flex-1"
                    value={social.url}
                    placeholder="https://x.com/…"
                    onChange={(e) =>
                      setFooterSocials((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)),
                      )
                    }
                  />
                  <button
                    type="button"
                    className="text-sm text-danger hover:text-danger"
                    onClick={() => setFooterSocials((prev) => prev.filter((_, j) => j !== i))}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="text-xs font-semibold text-accent hover:text-ink"
                onClick={() =>
                  setFooterSocials((prev) => [...prev, { platform: '', url: 'https://' }])
                }
              >
                + Add social link
              </button>
            </div>
            <div>
              <label className="field-label">Mailing address</label>
              <input
                className="field-input"
                value={footerAddress}
                placeholder="123 Main St, Springfield"
                onChange={(e) => setFooterAddress(e.target.value)}
              />
            </div>
            <div>
              <label className="field-label">Legal text</label>
              <input
                className="field-input"
                value={footerLegal}
                placeholder="© 2026 Acme Inc. All rights reserved."
                onChange={(e) => setFooterLegal(e.target.value)}
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm text-ink-soft">
                <input
                  type="checkbox"
                  checked={footerShowUnsubscribe}
                  onChange={(e) => setFooterShowUnsubscribe(e.target.checked)}
                />
                Unsubscribe link
              </label>
              <p className="mt-1 text-xs text-muted">
                Emails with no footer content of their own always get an unsubscribe link. Once you
                build footer content, that content must contain{' '}
                <code className="font-mono">{'{{unsubscribe_url}}'}</code> — turning this off is the
                only way to send without one, and bulk mail without an opt-out is illegal in most
                jurisdictions.
              </p>
            </div>
          </div>
        </section>

        {/* Save button */}
        <div className="flex justify-end border-t border-line pt-4">
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="btn-primary"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
